import { compact, sortBy, uniq } from "lodash";
import {
  OverallGoalAssessmentAnswerNode,
  OverallGoalAssessmentQuestionNode,
  OverallRecognitionCoreValueAssessmentAnswerNode,
  OverallRecognitionCoreValueAssessmentQuestionNode,
  RangeAssessmentAnswerNode,
  RangeAssessmentQuestionNode,
  TalentIndicatorAssessmentAnswerNode,
  TalentIndicatorAssessmentQuestionNode,
} from "types/graphql-schema";

import { TableSortDir } from "@components/table/table-constants";

import {
  AssessmentReportFilterMatchType,
  AssessmentReportSort,
  AssessmentReportSortKey,
  AssessmentReportSortType,
  AssessmentReportTableColumn,
  AssessmentReportViewOptions,
} from "./report-filters/types";

type AssessmentReportQuestion =
  | Partial<RangeAssessmentQuestionNode>
  | Partial<TalentIndicatorAssessmentQuestionNode>
  | Partial<OverallGoalAssessmentQuestionNode>
  | Partial<OverallRecognitionCoreValueAssessmentQuestionNode>;
export const isAssessmentReportQuestionType = (
  // type gate function so `any` is acceptable here

  question: any,
): question is AssessmentReportQuestion => {
  return (
    question.__typename === "RangeAssessmentQuestionNode" ||
    question.__typename === "TalentIndicatorAssessmentQuestionNode" ||
    question.__typename === "OverallGoalAssessmentQuestionNode" ||
    question.__typename === "OverallRecognitionCoreValueAssessmentQuestionNode"
  );
};

type AssessmentReportAnswer =
  | Partial<RangeAssessmentAnswerNode>
  | Partial<TalentIndicatorAssessmentAnswerNode>
  | Partial<OverallGoalAssessmentAnswerNode>
  | Partial<OverallRecognitionCoreValueAssessmentAnswerNode>;
export const isAssessmentReportAnswerType = (
  // type gate function so `any` is acceptable here

  answer: any,
): answer is AssessmentReportAnswer => {
  return (
    answer.__typename === "RangeAssessmentAnswerNode" ||
    answer.__typename === "TalentIndicatorAssessmentAnswerNode" ||
    answer.__typename === "OverallGoalAssessmentAnswerNode" ||
    answer.__typename === "OverallRecognitionCoreValueAssessmentAnswerNode"
  );
};

export const initAssessmentReportViewOptions: AssessmentReportViewOptions = {
  id: null,
  name: "New View",
  isDirty: false,
  filters: {
    type: AssessmentReportFilterMatchType.MATCH_ALL,
    list: [],
  },
  tableColumns: [],
  hiddenRecentAnswers: [],
  nineBox: {
    selectedXQuestionIndex: 0,
    selectedYQuestionIndex: 1,
  },
};

/**
 * The columns in the order of the saved view. A column the view does not list
 * keeps its default position.
 */
export const orderTableColumns = (
  tableColumns: AssessmentReportTableColumn[],
  viewOptionsTableColumns: AssessmentReportViewOptions["tableColumns"],
) => {
  const defaultPositions = tableColumns.reduce(
    (acc, col, index) => {
      acc[col.id] = index;
      return acc;
    },
    {} as Record<string, number>,
  );
  return sortBy(tableColumns, (col) => {
    const colIndex = viewOptionsTableColumns.findIndex(
      ({ id }) => id === col.id,
    );
    return colIndex === -1 ? defaultPositions[col.id] : colIndex;
  });
};

/**
 * The columns the table shows, in order. By default the saved view hides and
 * reorders them. With `fixedColumnIds`, the table shows exactly those columns,
 * in that order, and the saved view does not change them. An empty
 * `fixedColumnIds` counts as not set.
 */
export const getVisibleTableColumns = (
  tableColumns: AssessmentReportTableColumn[],
  viewOptionsTableColumns: AssessmentReportViewOptions["tableColumns"],
  fixedColumnIds?: AssessmentReportTableColumn["id"][],
) => {
  if (fixedColumnIds && fixedColumnIds.length > 0) {
    return compact(
      uniq(fixedColumnIds).map((id) =>
        tableColumns.find((col) => col.id === id),
      ),
    );
  }
  const hiddenColumnIds = viewOptionsTableColumns
    .filter((col) => col.hidden)
    .map((col) => col.id);
  // Order before hiding: the default positions count the hidden columns too.
  return orderTableColumns(tableColumns, viewOptionsTableColumns).filter(
    (col) => !hiddenColumnIds.includes(col.id),
  );
};

export type AssessmentReportSortState = {
  key: AssessmentReportSort;
  dir: TableSortDir;
};

export const isSameSort = (a: AssessmentReportSort, b: AssessmentReportSort) =>
  a.type === b.type &&
  (a.type !== AssessmentReportSortType.KEY ||
    (b.type === AssessmentReportSortType.KEY && a.key === b.key)) &&
  (a.type !== AssessmentReportSortType.QUESTION_ID ||
    (b.type === AssessmentReportSortType.QUESTION_ID &&
      a.questionId === b.questionId));

/**
 * The sort the rows use. By default an ongoing review sorts by Enrollment Date,
 * newest first, and other reviews sort by Name. The sort the user picked wins.
 * With fixed columns, a sort applies only while its column is shown, so rows
 * never sort by a column the table does not show. When the fixed columns hide
 * both defaults, the rows sort by the first column.
 */
export const getActiveSort = ({
  pickedSort,
  visibleColumns,
  ongoing,
  hasFixedColumns,
}: {
  pickedSort: AssessmentReportSortState | null;
  visibleColumns: AssessmentReportTableColumn[];
  ongoing: boolean;
  hasFixedColumns: boolean;
}): AssessmentReportSortState => {
  const canSortBy = (sort: AssessmentReportSort) =>
    !hasFixedColumns ||
    visibleColumns.some((col) => isSameSort(col.columnSortKey, sort));
  if (pickedSort && canSortBy(pickedSort.key)) {
    return pickedSort;
  }
  const enrollmentDateSort: AssessmentReportSort = {
    type: AssessmentReportSortType.KEY,
    key: AssessmentReportSortKey.ENROLLMENT_DATE,
  };
  if (ongoing && canSortBy(enrollmentDateSort)) {
    return { key: enrollmentDateSort, dir: "desc" };
  }
  const nameSort: AssessmentReportSort = {
    type: AssessmentReportSortType.KEY,
    key: AssessmentReportSortKey.NAME,
  };
  if (canSortBy(nameSort) || visibleColumns.length === 0) {
    return { key: nameSort, dir: "asc" };
  }
  return { key: visibleColumns[0].columnSortKey, dir: "asc" };
};

/**
 * The questions whose previous-review answers the rows show. The saved view
 * hides some, except with fixed columns: the saved view does not change them.
 */
export const getVisibleRecentAnswerQuestionIds = (
  recentAnswerQuestionIds: number[],
  hiddenRecentAnswers: number[] | null | undefined,
  hasFixedColumns: boolean,
) =>
  hasFixedColumns
    ? recentAnswerQuestionIds
    : recentAnswerQuestionIds.filter(
        (questionId) => !hiddenRecentAnswers?.includes(questionId),
      );

export type UserCalibrationGroups = {
  /** The group the person is calibrated in: one per review. */
  group?: { id: number; name: string };
  approverGroupIds: Set<number>;
  managerGroupIds: Set<number>;
};

/** Each person's calibration groups, read once for the whole table. */
export const getCalibrationGroupsByUserId = (
  groups: {
    id: number;
    name: string;
    users: { id: number }[];
    managers: { id: number }[];
    approvers: { id: number }[];
  }[],
) => {
  const byUserId = new Map<number, UserCalibrationGroups>();
  const entry = (userId: number) => {
    let found = byUserId.get(userId);
    if (!found) {
      found = { approverGroupIds: new Set(), managerGroupIds: new Set() };
      byUserId.set(userId, found);
    }
    return found;
  };
  groups.forEach((group) => {
    group.users.forEach(({ id }) => {
      const found = entry(id);
      found.group ??= { id: group.id, name: group.name };
    });
    group.approvers.forEach(({ id }) =>
      entry(id).approverGroupIds.add(group.id),
    );
    group.managers.forEach(({ id }) => entry(id).managerGroupIds.add(group.id));
  });
  return byUserId;
};
